import type { Chamado, ChamadoLocal, SituacaoChamado } from "../types";
import { naoVistasDo } from "./badge";

/**
 * Situações que tiram o chamado do caminho de todo dia. "Resolvido" entra junto de
 * "encerrado" e "cancelado" de propósito: para quem abriu, os três querem dizer a mesma
 * coisa — não há mais nada a acompanhar ali. (Resolvido ainda ACEITA mensagem: a seção
 * esconde da lista, não fecha a conversa.)
 */
const ENCERRADAS: SituacaoChamado[] = ["resolvido", "encerrado", "cancelado"];

/** Uma linha da lista: o que o storage guarda, o que o servidor sabe e a novidade. */
export interface ItemDaLista {
  local: ChamadoLocal;
  remoto?: Chamado;
  novas: number;
}

/**
 * Separa a lista do widget em "em aberto", "compartilhados" e "encerrados" (DEV-183 +
 * DEV-245) — os encerrados vão para uma seção colapsada no fim, em vez de empurrarem
 * para baixo o que ainda está vivo.
 *
 * Três regras decidem os casos de borda, e as duas primeiras puxam para MOSTRAR:
 *
 *  1. **Sem resumo do servidor, é aberto.** A lista é pintada na hora com o que está no
 *     localStorage (código e título) e o resumo chega depois; classificar pelo silêncio
 *     mandaria o chamado para dentro da seção fechada no primeiro frame — e para sempre,
 *     se a rede falhar.
 *  2. **Com resposta não vista, é aberto** — mesmo resolvido ou encerrado. A badge promete
 *     apontar para algo alcançável; mandar a novidade para dentro de uma seção fechada é
 *     exatamente o contrário, e é assim que uma resposta final ("resolvi assim, confere?")
 *     passaria despercebida.
 *  3. **Encerrado VENCE compartilhado.** Card compartilhado e já concluído vai para a
 *     seção do fim, com os outros encerrados: as duas primeiras seções são o que ainda
 *     pede atenção, e "compartilhado" diz de ONDE veio, não que ainda esteja vivo.
 *
 * A separação por origem existe pelo mesmo motivo que o portal tem duas abas: pôr um card
 * do time sob o título "Meus chamados" promete mais do que entrega.
 *
 * A ordem de dentro de cada grupo é a que chegou (o resumo já vem por atividade recente).
 */
export function separarChamados(
  locais: ChamadoLocal[],
  remotos: Chamado[],
): { abertos: ItemDaLista[]; compartilhados: ItemDaLista[]; encerrados: ItemDaLista[] } {
  const abertos: ItemDaLista[] = [];
  const compartilhados: ItemDaLista[] = [];
  const encerrados: ItemDaLista[] = [];

  for (const local of locais) {
    const remoto = remotos.find((r) => r.codigo === local.codigo);
    const novas = remoto ? naoVistasDo(local, remoto) : 0;
    const item: ItemDaLista = { local, remoto, novas };
    if (remoto !== undefined && novas === 0 && ENCERRADAS.includes(remoto.situacao)) {
      encerrados.push(item);
      continue;
    }
    // O resumo manda; sem ele vale o que o storage guardou (é o primeiro frame).
    const ehCompartilhado = remoto ? remoto.compartilhado : local.compartilhado === true;
    (ehCompartilhado ? compartilhados : abertos).push(item);
  }

  return { abertos, compartilhados, encerrados };
}
